// Centralized image and content data for Social Proof sections
// Add new images here — they will automatically appear in the relevant sections

export const heroImages = [
  '/images/tapestry-training.jpg',
  '/images/tapestry-group.jpg',
  '/images/corporate-boardroom.jpg',
  '/images/top-tech-award.jpg',
];

export const socialProofImages = [
  {
    src: '/images/tapestry-training.jpg',
    title: 'Team Dynamics Workshop',
    subtitle: 'Tapestry | Las Vegas',
    outcome: 'Strengthened cross-functional collaboration through DISC-based team dynamics training',
    category: 'corporate',
  },
  {
    src: '/images/top-tech-award.jpg',
    title: 'Top Tech Award Winner',
    subtitle: 'Allegiant Stadium | Las Vegas',
    outcome: 'Recognized for excellence in technology and leadership innovation',
    category: 'recognition',
  },
  {
    src: '/images/sbdc-seminar.jpg',
    title: 'Club CORE Business Planning',
    subtitle: 'SBDC | Q2 Business Seminar',
    outcome: 'Guided entrepreneurs through strategic business planning and execution',
    category: 'education',
  },
  {
    src: '/images/executive-seminar.jpg',
    title: 'Executive Leadership Seminar',
    subtitle: 'Government & Civic Leaders',
    outcome: 'Enhanced strategic leadership capacity across departments',
    category: 'corporate',
  },
  {
    src: '/images/corporate-boardroom.jpg',
    title: 'CATCH Leadership Program',
    subtitle: 'Corporate Team | Las Vegas',
    outcome: 'Improved communication and team alignment',
    category: 'corporate',
  },
  {
    src: '/images/sumnu-workshop.jpg',
    title: 'SUMNU Marketing Training',
    subtitle: 'Do Sumnu Team | WE Mentality',
    outcome: 'Built leadership capacity and team alignment for marketing professionals',
    category: 'community',
  },
  {
    src: '/images/leader-in-you.jpg',
    title: 'The Leader In You Workshop',
    subtitle: 'SUMNU Marketing Team',
    outcome: 'Unlocked individual leadership potential across the organization',
    category: 'community',
  },
  {
    src: '/images/alignment-workshop.jpg',
    title: 'Team Alignment Workshop',
    subtitle: 'Workforce Development Program',
    outcome: 'Created shared vision and reduced workplace friction',
    category: 'workforce',
  },
  {
    src: '/images/group-exercise.jpg',
    title: 'Small Group Exercise',
    subtitle: 'Professional Development Session',
    outcome: 'Hands-on collaboration building communication and trust',
    category: 'workforce',
  },
  {
    src: '/images/disc-assessment.jpg',
    title: 'DISC Assessment Training',
    subtitle: 'Corporate Team | Las Vegas',
    outcome: 'Built self-awareness and adaptive communication skills',
    category: 'corporate',
  },
  {
    src: '/images/workshop-training.jpg',
    title: 'Interactive Training Session',
    subtitle: 'Community Organization',
    outcome: 'Engaged participants in hands-on leadership development',
    category: 'community',
  },
  {
    src: '/images/team-exercise.jpg',
    title: 'Team Presentation Exercise',
    subtitle: 'Professional Development Program',
    outcome: 'Developed communication confidence and team collaboration',
    category: 'education',
  },
];

export const inActionImages = [
  { src: '/images/alignment-workshop.jpg', label: 'Team Alignment' },
  { src: '/images/tapestry-training.jpg', label: 'Team Dynamics Training' },
  { src: '/images/sbdc-seminar.jpg', label: 'Business Seminar' },
  { src: '/images/sumnu-workshop.jpg', label: 'Workshop Facilitation' },
];

export const industryCategories = [
  {
    name: 'Corporate',
    description: 'Fortune 500 companies and growing businesses',
    images: [
      { src: '/images/tapestry-training.jpg', label: 'Team Dynamics Training', metric: '40% reduction in turnover' },
      { src: '/images/tapestry-group.jpg', label: 'Corporate Team Building', metric: '92% report improved collaboration' },
      { src: '/images/corporate-boardroom.jpg', label: 'Leadership Workshop', metric: 'Adaptive communication mastery' },
      { src: '/images/disc-assessment.jpg', label: 'DISC Assessment', metric: 'Self-awareness & team alignment' },
    ],
  },
  {
    name: 'Workforce Development',
    description: 'Skills training and career advancement programs',
    images: [
      { src: '/images/alignment-workshop.jpg', label: 'Team Alignment', metric: 'Shared vision & reduced friction' },
      { src: '/images/group-exercise.jpg', label: 'Group Exercise', metric: 'Hands-on skill development' },
      { src: '/images/workshop-training.jpg', label: 'Professional Training', metric: '94% program completion rate' },
    ],
  },
  {
    name: 'Education',
    description: 'Universities, schools, and educational institutions',
    images: [
      { src: '/images/sbdc-seminar.jpg', label: 'SBDC Business Planning', metric: 'Strategic execution planning' },
      { src: '/images/team-exercise.jpg', label: 'Student Development', metric: 'Communication confidence' },
      { src: '/images/leader-in-you.jpg', label: 'Youth Leadership', metric: 'Emerging leader development' },
    ],
  },
  {
    name: 'Community Organizations',
    description: 'Nonprofits, government, and civic groups',
    images: [
      { src: '/images/sumnu-workshop.jpg', label: 'Community Workshop', metric: 'Leadership at every level' },
      { src: '/images/executive-seminar.jpg', label: 'Civic Leadership', metric: '85% promoted within 12 months' },
      { src: '/images/leader-in-you.jpg', label: 'Community Training', metric: 'Lasting community impact' },
    ],
  },
];

export const caseStudies = [
  {
    image: '/images/tapestry-training.jpg',
    industry: 'Corporate',
    title: 'Global Retail Brand Leadership Transformation',
    before: 'High turnover among frontline supervisors, inconsistent communication across departments, and declining employee engagement scores.',
    approach: 'Delivered a multi-session leadership development program featuring DISC behavioral assessments, adaptive communication training, and team alignment workshops.',
    results: [
      '40% reduction in supervisor turnover within 6 months',
      'Employee engagement scores increased by 28%',
      'Cross-department communication rated "significantly improved" by 92% of participants',
    ],
  },
  {
    image: '/images/sbdc-seminar.jpg',
    industry: 'Workforce Development',
    title: 'Workforce Training Program with AI Integration',
    before: 'Outdated training methods, low completion rates among participants, and difficulty measuring program impact.',
    approach: 'Redesigned the training curriculum with hands-on workshops, integrated AI-driven learning tools, and implemented measurable performance benchmarks.',
    results: [
      'Program completion rate increased from 62% to 94%',
      'Participants reported 3x higher confidence in applying new skills',
      'Organization secured additional workforce development funding based on documented outcomes',
    ],
  },
  {
    image: '/images/executive-seminar.jpg',
    industry: 'Government & Education',
    title: 'Leadership Development for Public Sector Leaders',
    before: 'Siloed departments, low morale among mid-level managers, and a lack of succession planning.',
    approach: 'Conducted executive coaching sessions, facilitated cross-departmental alignment workshops, and built a leadership pipeline framework with coaching support.',
    results: [
      '85% of participants promoted or given expanded leadership roles within 12 months',
      'Internal satisfaction surveys improved by 35%',
      'Established a sustainable leadership pipeline reducing external hiring by 50%',
    ],
  },
];

export const featuredTestimonial = {
  image: '/images/testimonial-heather.png',
  quote: "This year's leadership training was the most powerful and impactful one yet. Thank you Brandon for bringing your strengths and science to my team. We are aligned, united, and committed to leaning into the adaptive communication you taught us. My heart is full of gratitude!!",
  author: 'Heather Avila',
  title: 'President & CEO',
  organization: 'Live Electric Inc.',
};

export const secondTestimonial = {
  image: '/images/testimonial-tiara.png',
  quote: "This was one of the best retreats we've ever had. The facilitation was perfect and the balance between presentation and engagement was strong. What stood out most was the follow up and follow through. We've done many assessments before, but never experienced this level of impact.",
  author: 'Tiara Flynn',
  title: 'Director',
  organization: 'SUMNU Marketing',
};
